import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  IconButton,
  Collapse,
  Typography,
  Pagination,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
} from "@mui/material";
import React, { useState } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Eye,
  MapPin,
  Package,
  User,
  Phone,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import BackBtn from "../widgets/BackBtn";
import useApiEp from "../hooks/useApiEp";

const ToolsOrders = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const endpoint = `admin/get-tools-orders?page=${page}&order_id=${searchTerm}&status=${
    statusFilter === "all" ? "" : statusFilter
  }&paymentStatus=${typeFilter === "all" ? "" : typeFilter}&date=${dateFilter}`;

  const { data, isLoading, isError, error } = useApiEp(endpoint);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    setExpandedRow(null);
  };

  const handleFilterChange = () => {
    setPage(1);
    setExpandedRow(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    handleFilterChange();
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    handleFilterChange();
  };

  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
    handleFilterChange();
  };

  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
    handleFilterChange();
  };

  const toggleRow = (orderId) => {
    setExpandedRow(expandedRow === orderId ? null : orderId);
  };

  const handleViewOrder = (orderId) => {
    navigate(`/home/tools-order/${orderId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      accepted: "success",
      shipped: "info",
      delivered: "primary",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  if (isLoading)
    return (
      <Container sx={{ mt: 3 }}>
        <Typography>Loading orders...</Typography>
      </Container>
    );
  if (isError)
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error">Error: {error?.message}</Alert>
      </Container>
    );

  const orders = data?.data || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <BackBtn />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Tools Orders
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track all tool orders from your customers
        </Typography>
      </Box>

      {/* Filters Section */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Order ID"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <Search size={20} color="action.active" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={typeFilter}
                  label="Payment Status"
                  onChange={handleTypeChange}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Order Date"
                type="date"
                value={dateFilter}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  label={`Total: ${data?.totalItems || 0}`}
                  icon={<DollarSign size={16} />}
                  variant="outlined"
                  color="primary"
                />
                <Chip
                  label={`Pages: ${data?.totalPages || 1}`}
                  icon={<Calendar size={16} />}
                  variant="outlined"
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "15%" }}
                >
                  Order ID
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "12%" }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "10%" }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "10%" }}
                >
                  Items
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "12%" }}
                >
                  Total
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "15%" }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "15%" }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{ color: "white", fontWeight: "bold", width: "11%" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Package size={48} color="text.secondary" />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <React.Fragment key={order._id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.orderId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {new Date(order.createdOn).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(order.createdOn).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.orderType || "purchase"}
                          size="small"
                          variant="outlined"
                          color={
                            order.orderType === "rental"
                              ? "secondary"
                              : "primary"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Package size={16} />
                          <Typography>
                            {order.orderSummary.itemCount}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color="primary">
                          ₹{order.orderSummary.total.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.orderStatus}
                          color={getStatusColor(order.orderStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {order.shippingAddress?.fullName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            onClick={() => handleViewOrder(order.orderId)}
                            size="small"
                            color="primary"
                            title="View Order Details"
                          >
                            <Eye size={18} />
                          </IconButton>
                          <IconButton
                            onClick={() => toggleRow(order.orderId)}
                            size="small"
                            color="info"
                            title="Quick View"
                          >
                            {expandedRow === order.orderId ? (
                              <ChevronUp size={18} />
                            ) : (
                              <ChevronDown size={18} />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse
                          in={expandedRow === order.orderId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              p: 3,
                              backgroundColor: "grey.50",
                              borderTop: 1,
                              borderColor: "grey.200",
                            }}
                          >
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Package size={20} />
                                  Order Items
                                </Typography>
                                <Stack spacing={2}>
                                  {order.items.map((item, idx) => (
                                    <Card key={idx} variant="outlined">
                                      <CardContent sx={{ p: 2 }}>
                                        <Grid
                                          container
                                          spacing={2}
                                          alignItems="center"
                                        >
                                          <Grid item xs={3}>
                                            {item.images && item.images[0] && (
                                              <img
                                                src={item.images[0]}
                                                alt={item.name}
                                                style={{
                                                  width: "100%",
                                                  height: 80,
                                                  objectFit: "cover",
                                                  borderRadius: 8,
                                                }}
                                              />
                                            )}
                                          </Grid>
                                          <Grid item xs={9}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight="bold"
                                            >
                                              {item.name}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              Quantity: {item.quantity} × ₹
                                              {item.price}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              fontWeight="bold"
                                              color="primary"
                                            >
                                              Total: ₹{item.itemTotal}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Stack>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <User size={20} />
                                      Customer & Shipping
                                    </Typography>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Stack spacing={1}>
                                          <Typography variant="body2">
                                            <strong>Name:</strong>{" "}
                                            {order.shippingAddress.fullName}
                                          </Typography>
                                          <Typography variant="body2">
                                            <strong>Phone:</strong>{" "}
                                            {order.shippingAddress.phone}
                                          </Typography>
                                          <Typography variant="body2">
                                            <strong>Address:</strong>{" "}
                                            {order.shippingAddress.address},{" "}
                                            {order.shippingAddress.city},{" "}
                                            {order.shippingAddress.state} -{" "}
                                            {order.shippingAddress.zipCode}
                                          </Typography>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <DollarSign size={20} />
                                      Order Summary
                                    </Typography>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Stack spacing={1}>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <Typography variant="body2">
                                              Subtotal:
                                            </Typography>
                                            <Typography variant="body2">
                                              ₹{order.orderSummary.subtotal}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <Typography variant="body2">
                                              GST:
                                            </Typography>
                                            <Typography variant="body2">
                                              ₹{order.orderSummary.gst}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <Typography variant="body2">
                                              Shipping:
                                            </Typography>
                                            <Typography variant="body2">
                                              ₹
                                              {(
                                                order.orderSummary.shipping || 0
                                              ).toFixed(2)}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              borderTop: 1,
                                              borderColor: "grey.300",
                                              pt: 1,
                                            }}
                                          >
                                            <Typography
                                              variant="body1"
                                              fontWeight="bold"
                                            >
                                              Grand Total:
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              fontWeight="bold"
                                              color="primary"
                                            >
                                              ₹
                                              {order.orderSummary.total.toFixed(
                                                2
                                              )}
                                            </Typography>
                                          </Box>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {data?.totalItems > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {orders.length} of {data?.totalItems} orders
          </Typography>
          <Pagination
            count={data?.totalPages || 1}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Container>
  );
};

export default ToolsOrders;
